/**
 * Shared locale primitives (Story 3.6, AC-1/3).
 *
 * The SINGLE cross-workspace source of truth for the 7 supported locales, so the
 * server (Epic 3) and the widget (Epic 4) both import this list rather than
 * re-deriving it (coding-standards: "define shared types in `packages/shared`;
 * never redefine locally").
 *
 * This module holds CONSTANTS + pure helpers — not a redefinition of a shared
 * DATA type (`Chunk`/`Source`/`StructuredResponse`) — so it correctly lives here
 * without conflicting with the "never redefine a shared data type" rule.
 *
 * NO new npm dependency: `resolveLocale` is hand-rolled (region-strip + EN
 * fallback); all localized strings are inline UTF-8 literals.
 */

/**
 * The 7 supported locales, as BCP-47 primary subtags: English, French, Spanish,
 * Indonesian, Punjabi, Ukrainian, Tagalog. This is the authoritative product
 * constant (NOT an env var — it must not vary per environment).
 */
export const SUPPORTED_LOCALES = ['en', 'fr', 'es', 'id', 'pa', 'uk', 'tl'] as const;

export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

/**
 * Per-locale metadata. `englishName` drives the grounding language directive
 * (grounding.ts `languageDirective`); `nativeName` is available to the Epic-4
 * widget language menu.
 */
export const LOCALE_INFO: Record<SupportedLocale, { englishName: string; nativeName: string }> = {
  en: { englishName: 'English', nativeName: 'English' },
  fr: { englishName: 'French', nativeName: 'Français' },
  es: { englishName: 'Spanish', nativeName: 'Español' },
  id: { englishName: 'Indonesian', nativeName: 'Bahasa Indonesia' },
  pa: { englishName: 'Punjabi', nativeName: 'ਪੰਜਾਬੀ' },
  uk: { englishName: 'Ukrainian', nativeName: 'Українська' },
  tl: { englishName: 'Tagalog', nativeName: 'Tagalog' },
};

/** Membership test against `SUPPORTED_LOCALES`. */
export function isSupportedLocale(x: string): x is SupportedLocale {
  return (SUPPORTED_LOCALES as readonly string[]).includes(x);
}

/**
 * The AC-1 normalizer: the ONE place an arbitrary request/handshake locale
 * becomes one of the 7 (or the EN fallback). Lowercases, takes the primary
 * subtag before a `-`/`_` separator (so `en-US`→`en`, `fr-CA`→`fr`,
 * `pa-Guru`→`pa`), returns it if supported, else falls back to `'en'`.
 */
export function resolveLocale(requested: string | undefined): SupportedLocale {
  if (requested === undefined) {
    return 'en';
  }
  const primary = requested.toLowerCase().split(/[-_]/)[0] ?? '';
  return isSupportedLocale(primary) ? primary : 'en';
}

/**
 * The localized "Source" citation label (AC-3). Routed through `resolveLocale`,
 * so an unknown locale yields the EN label. NEVER touches `Source.url` — this is
 * the fixed UI word the widget renders as "Source N: {reference}", not the URL
 * and not the model-produced `reference`.
 *
 * Draft strings — flag for native-speaker / PO review; the wire-up is the code
 * deliverable, the exact phrasing is reviewable data.
 */
const CITATION_LABEL: Record<SupportedLocale, string> = {
  en: 'Source',
  fr: 'Source',
  es: 'Fuente',
  id: 'Sumber',
  pa: 'ਸਰੋਤ',
  uk: 'Джерело',
  tl: 'Pinagkunan',
};

export function citationLabel(locale: string): string {
  return CITATION_LABEL[resolveLocale(locale)];
}
